import React, { useState, useRef, useId } from 'react';
import RichContent from './RichContent';
import { 
  Bold, Italic, Underline, List, ListOrdered, 
  Heading2, Heading3, Quote, Eye, Edit3
} from 'lucide-react';

const RichTextEditor = ({ value = '', onChange, placeholder = 'Write details here...', label, helpText }) => {
  const [isPreview, setIsPreview] = useState(false);
  const textareaRef = useRef(null);
  const editorId = useId();

  const applyFormat = (tag, endTag = null) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const beforeText = textarea.value.substring(0, start);
    const afterText = textarea.value.substring(end);

    let replacement = '';
    const closing = endTag || `</${tag.replace(/<|\/|>| /g, '').split(' ')[0]}>`;

    if (tag === 'ul') {
      const items = selectedText ? selectedText.split('\n') : ['Item 1', 'Item 2'];
      replacement = `<ul>\n${items.map(i => `  <li>${i.replace(/^[-*•]\s*/, '')}</li>`).join('\n')}\n</ul>`;
    } else if (tag === 'ol') {
      const items = selectedText ? selectedText.split('\n') : ['Step 1', 'Step 2'];
      replacement = `<ol>\n${items.map(i => `  <li>${i.replace(/^\d+\.\s*/, '')}</li>`).join('\n')}\n</ol>`;
    } else if (tag === 'h2') {
      replacement = `<h2>${selectedText || 'Heading'}</h2>`;
    } else if (tag === 'h3') {
      replacement = `<h3>${selectedText || 'Subheading'}</h3>`;
    } else if (tag === 'p') {
      replacement = `<p>${selectedText || 'Paragraph text'}</p>`;
    } else {
      replacement = `<${tag}>${selectedText || 'text'}${closing}`;
    }

    const newContent = `${beforeText}${replacement}${afterText}`;
    onChange(newContent);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + replacement.length, start + replacement.length);
    }, 10);
  };

  return (
    <div className="space-y-1.5 w-full min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {label && (
          <label htmlFor={editorId} className="block text-xs font-bold text-gray-800 tracking-wide">
            {label}
          </label>
        )}
        <button
          type="button"
          onClick={() => setIsPreview(!isPreview)}
          className="text-xs text-[#6E1B34] hover:text-[#D9711F] font-semibold inline-flex items-center gap-1 transition-colors"
        >
          {isPreview ? <Edit3 className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          <span>{isPreview ? 'Edit Code / Text' : 'Preview HTML'}</span>
        </button>
      </div>

      <div className="border border-[#E7D9BC] rounded-xl overflow-hidden bg-white shadow-xs focus-within:border-[#D9711F] focus-within:ring-2 focus-within:ring-[#D9711F]/20 transition-all">
        {/* Toolbar */}
        {!isPreview && (
          <div className="flex flex-wrap items-center gap-1 p-2 bg-[#FBF4E7]/70 border-b border-[#E7D9BC] text-gray-700">
            <button
              type="button"
              onClick={() => applyFormat('strong', '</strong>')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Bold"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => applyFormat('em', '</em>')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Italic"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => applyFormat('u', '</u>')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Underline"
            >
              <Underline className="w-4 h-4" />
            </button>

            <span className="w-px h-4 bg-[#E7D9BC] mx-1"></span>

            <button
              type="button"
              onClick={() => applyFormat('h2')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Heading 2"
            >
              <Heading2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => applyFormat('h3')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Heading 3"
            >
              <Heading3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => applyFormat('p')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-xs font-bold text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Paragraph"
            >
              P
            </button>

            <span className="w-px h-4 bg-[#E7D9BC] mx-1"></span>

            <button
              type="button"
              onClick={() => applyFormat('ul')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => applyFormat('ol')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Numbered List"
            >
              <ListOrdered className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => applyFormat('blockquote')}
              className="p-1.5 hover:bg-[#F3E7D0] rounded-lg text-gray-700 hover:text-[#6E1B34] transition-colors"
              title="Quote"
            >
              <Quote className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Content Area */}
        {isPreview ? (
          <RichContent
            className="p-3.5 min-h-[140px] text-sm text-gray-800 bg-[#FAFAFA]"
            value={value || 'No content entered yet'}
          />
        ) : (
          <textarea
            id={editorId}
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full p-3 min-h-[140px] text-xs sm:text-sm text-[#2A1F16] placeholder-gray-400 border-0 focus:ring-0 focus:outline-none resize-y font-mono"
            rows={5}
          />
        )}
      </div>

      {helpText && (
        <p className="text-[11px] text-gray-500">{helpText}</p>
      )}
    </div>
  );
};

export default RichTextEditor;
